import { useCallback } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Client } from '../../types';

export interface ClientFormValues extends Partial<Client> {}
export interface EmailValues {
  email: string;
}
export interface PasswordValues {
  password: string;
  passwordConfirm: string;
}

// Mirror of express/lib/phone.js: E.164 ("+642885146670") or null when the
// input isn't a real number. Accepts a national leading zero, spacing, and a
// duplicated country code ("+64 02...").
export const normalisePhoneNumber = (input: string | null | undefined): string | null => {
  if (!input) return null;
  const raw = String(input)
    .trim()
    .replace(/^(\+?64)\s*0/, '$1');
  if (!raw) return null;
  let parsed = parsePhoneNumberFromString(raw, 'NZ');
  // No "+" and not a valid NZ national number: most likely an international
  // number typed without the "+" (e.g. "61412345678").
  if ((!parsed || !parsed.isValid()) && /^\d/.test(raw)) {
    parsed = parsePhoneNumberFromString(`+${raw}`);
  }
  if (!parsed || !parsed.isValid()) return null;
  return parsed.number;
};

export default function useValidators() {
  const isPhoneNumberValid = useCallback((phoneNumber: string) => normalisePhoneNumber(phoneNumber) !== null, []);

  const isEmailValid = useCallback((email: string) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email), []);

  const validateEmail = useCallback(
    (values: EmailValues) => {
      const errors: Partial<EmailValues> = {};
      if (!values.email) {
        errors.email = 'Required';
      } else if (!isEmailValid(values.email)) {
        errors.email = 'Invalid email address';
      }
      return errors;
    },
    [isEmailValid]
  );

  const validatePasswords = useCallback((values: PasswordValues) => {
    const errors: Partial<PasswordValues> = {};
    if (values.password && values.password.length < 8) {
      errors.password = 'Password Minimum 8 Characters';
    } else if (values.password !== values.passwordConfirm) {
      errors.passwordConfirm = 'Password must match';
    }
    return errors;
  }, []);

  const validateClient = useCallback((values: ClientFormValues) => {
    const errors: { [Property in keyof Partial<Client>]: string } = {};
    const requiredFields: (keyof ClientFormValues)[] = ['companyName', 'address'];
    for (const field of requiredFields) {
      if (!values[field]) {
        errors[field] = 'Required';
        break;
      }
    }
    return errors;
  }, []);

  return { isEmailValid, isPhoneNumberValid, normalisePhoneNumber, validateClient, validateEmail, validatePasswords };
}
